import React from "react";
import { useNavigate } from "react-router-dom";
import "./EsimCard.css";
import { useLanguage } from "../../i18n/LanguageContext";

// Flag lookup (flagcdn). Multi-country regions fall back to no flag.
const FLAG_OVERRIDES = { eur: "eu", eu: "eu", uk: "gb" };
const flagUrl = (code) => {
    if (!code) return null;
    const c = String(code).toLowerCase();
    const mapped = c in FLAG_OVERRIDES ? FLAG_OVERRIDES[c] : (c.length === 2 ? c : null);
    return mapped ? `https://flagcdn.com/w80/${mapped}.png` : null;
};

// `key` is the label under esimCard.status.
const STATUS = {
    // Set at issuance, before the provider reports any usage.
    ESIM_CREATED: { key: "created",   cls: "created" },
    SIM_CREATED:  { key: "created",   cls: "created" },
    // Reported by the provider's lifecycle notifications.
    IN_USE:       { key: "inUse",     cls: "active" },
    USED_UP:      { key: "usedUp",    cls: "terminated" },
    EXPIRED:      { key: "expired",   cls: "terminated" },
    CANCELLED:    { key: "cancelled", cls: "cancelled" },
};
// Unknown status must never fall back to "Aktif" - that would label an expired
// or cancelled eSIM as live.
const statusOf = (s) => STATUS[String(s || "").toUpperCase()] || { key: null, cls: "created" };

const IcSim = () => (
    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <rect x="4" y="3" width="16" height="18" rx="3" stroke="#8c8c8c" strokeWidth="2" />
        <rect x="9" y="11" width="6" height="6" rx="1" stroke="#8c8c8c" strokeWidth="2" />
    </svg>
);
const IcCal = () => (
    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <rect x="3" y="5" width="18" height="16" rx="3" stroke="#8c8c8c" strokeWidth="2" />
        <path d="M3 9h18M8 3v4M16 3v4" stroke="#8c8c8c" strokeWidth="2" strokeLinecap="round" />
    </svg>
);
const IcPlus = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <circle cx="12" cy="12" r="9" stroke="#fff" strokeWidth="2" />
        <path d="M12 8v8M8 12h8" stroke="#fff" strokeWidth="2" strokeLinecap="round" />
    </svg>
);

const EsimCard = ({ item, openTopUpModal }) => {
    const navigate = useNavigate();
    const { t, locale } = useLanguage();
    const region = item.firstRegion || {};
    const thumb = region.shopImageUrl || region.imageUrl;
    const flag = flagUrl(region.regionCode);
    const st = statusOf(item.status);
    // simType 0 is a physical SIM card; anything else (or a missing value) is an
    // eSIM. parseInt handles the field arriving as either "0" or 0.
    const prefix = parseInt(item.simType, 10) === 0 ? "SIM" : "eSIM";
    const name = item.label || `${prefix} ${region.name || region.regionCode?.toUpperCase() || ""}`.trim();
    const purchaseDate = item.createdAt
        ? new Date(item.createdAt).toLocaleDateString(locale, { year: "numeric", month: "short", day: "numeric" })
        : null;

    const goDetails = () => { if (item.SimId) navigate(`/sim/${encodeURIComponent(item.SimId)}`); };
    const onKey = (e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); goDetails(); } };
    const stop = (e) => e.stopPropagation();

    return (
        <div className="ms-card" role="button" tabIndex={0} onClick={goDetails} onKeyDown={onKey}>
            <div className="ms-card-qr" aria-hidden="true">
                <svg viewBox="0 0 120 120" fill="none" xmlns="http://www.w3.org/2000/svg">
                    {[0, 30, 60, 90].map((y) =>
                        [0, 30, 60, 90].map((x) => (
                            <path key={`${x}-${y}`} d={`M${x} ${y + 30}a15 15 0 0 1 15 -15v15z`} fill="#237804" opacity="0.05" />
                        ))
                    )}
                </svg>
            </div>

            <div className="ms-card-top">
                <div className="ms-card-thumb-wrap">
                    {thumb
                        ? <img className="ms-card-thumb" src={thumb} alt="" loading="lazy" />
                        : <div className="ms-card-thumb ms-card-thumb-ph" />}
                </div>
                <div className="ms-card-id">
                    {flag && <img className="ms-card-flag" src={flag} alt="" loading="lazy" />}
                    <span className="ms-card-name">{name}</span>
                </div>
                <span className={`ms-card-status ${st.cls}`}>{st.key ? t(`esimCard.status.${st.key}`) : "—"}</span>
            </div>

            <div className="ms-card-meta">
                <span className="ms-chip"><IcSim /> {t("esimCard.iccid", { iccid: item.iccid })}</span>
                {purchaseDate && <span className="ms-chip"><IcCal /> {t("esimCard.purchased", { date: purchaseDate })}</span>}
            </div>

            <div className="ms-card-actions">
                <button className="ms-btn ms-btn-outline" onClick={(e) => { stop(e); goDetails(); }}>
                    {t("esimCard.checkQuota")}
                </button>
                <button className="ms-btn ms-btn-primary" onClick={(e) => { stop(e); openTopUpModal(item.SimId, item.iccid); }}>
                    <IcPlus /> {t("common.topUp")}
                </button>
            </div>
        </div>
    );
};

export default EsimCard;
